'use client';

import React, { useState } from 'react';
import {
  X, Building2, MapPin, Phone, Mail, User, Users,
  Monitor, Server, Printer, Wifi, Network, Zap, Package,
  Laptop, FileText, Edit2, Calendar,
} from 'lucide-react';
import { type Client, type Equipment, type Intervention, CONNECTION_TYPE_COLORS, STATUS_COLORS } from '@/lib/data';

interface Props {
  client: Client;
  equipment: Equipment[];
  interventions: Intervention[];
  onClose: () => void;
  onEdit: (client: Client) => void;
}

type Tab = 'info' | 'contacts' | 'equipment' | 'interventions';

const EQ_ICONS: Record<string, React.ReactNode> = {
  Desktop: <Monitor size={16} />,
  Laptop: <Laptop size={16} />,
  Server: <Server size={16} />,
  Štampač: <Printer size={16} />,
  Switch: <Network size={16} />,
  Ruter: <Wifi size={16} />,
  UPS: <Zap size={16} />,
  Ostalo: <Package size={16} />,
};

const TABS: { id: Tab; label: string }[] = [
  { id: 'info', label: 'Opšte info' },
  { id: 'contacts', label: 'Zaposleni' },
  { id: 'equipment', label: 'Oprema' },
  { id: 'interventions', label: 'Intervencije' },
];

export default function ClientDetailPanel({ client, equipment, interventions, onClose, onEdit }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('info');

  function formatDate(d: string) {
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
  }

  return (
    <div
      className="w-[420px] min-w-[420px] border-l flex flex-col h-full animate-slide-in-right overflow-hidden"
      style={{
        backgroundColor: 'hsl(var(--card))',
        borderColor: 'hsl(var(--border))',
      }}
    >
      {/* Header */}
      <div
        className="flex items-start justify-between px-5 py-4 border-b"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold"
            style={{ backgroundColor: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))' }}
          >
            {client.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h2 className="font-semibold text-base leading-tight" style={{ color: 'hsl(var(--foreground))' }}>
              {client.name}
            </h2>
            <p className="text-xs mt-0.5 mono" style={{ color: 'hsl(var(--muted-foreground))' }}>
              PIB: {client.pib}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button className="btn-ghost p-1.5" title="Uredi klijenta" onClick={() => onEdit(client)}>
            <Edit2 size={15} />
          </button>
          <button className="btn-ghost p-1.5" title="Zatvori panel" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex border-b px-5 gap-1"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        {TABS.map(tab => (
          <button
            key={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-3 text-sm font-medium border-b-2 transition-all duration-150 ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600' :'border-transparent'
            }`}
            style={{
              color: activeTab === tab.id ? 'hsl(var(--accent))' : 'hsl(var(--muted-foreground))',
            }}
          >
            {tab.label}
            {tab.id === 'contacts' && (
              <span
                className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
              >
                {client.employees.length}
              </span>
            )}
            {tab.id === 'equipment' && (
              <span
                className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
              >
                {equipment.length}
              </span>
            )}
            {tab.id === 'interventions' && (
              <span
                className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'hsl(217 91% 60% / 0.10)', color: 'hsl(var(--accent))' }}
              >
                {interventions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
        {/* Info tab */}
        {activeTab === 'info' && (
          <div className="space-y-4">
            <div className="space-y-3">
              <InfoRow icon={<Building2 size={15} />} label="Naziv" value={client.name} />
              <InfoRow icon={<FileText size={15} />} label="PIB" value={<span className="mono">{client.pib}</span>} />
              <InfoRow icon={<MapPin size={15} />} label="Adresa" value={`${client.address}, ${client.city}`} />
              <InfoRow icon={<User size={15} />} label="Odgovorno lice" value={client.responsiblePerson} />
              <InfoRow icon={<Phone size={15} />} label="Telefon" value={
                <a href={`tel:${client.phone}`} className="hover:underline" style={{ color: 'hsl(var(--accent))' }}>
                  {client.phone}
                </a>
              } />
              <InfoRow icon={<Mail size={15} />} label="E-mail" value={
                <a href={`mailto:${client.email}`} className="hover:underline truncate block max-w-[220px]" style={{ color: 'hsl(var(--accent))' }}>
                  {client.email}
                </a>
              } />
              <InfoRow icon={<Calendar size={15} />} label="Klijent od" value={formatDate(client.createdAt)} />
            </div>
            {client.notes && (
              <div
                className="p-3 rounded-lg text-sm"
                style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
              >
                <p className="font-medium text-xs uppercase tracking-wide mb-1" style={{ color: 'hsl(var(--foreground))' }}>
                  Napomene
                </p>
                {client.notes}
              </div>
            )}
          </div>
        )}

        {/* Contacts tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-3">
            {client.employees.length === 0 ? (
              <div className="text-center py-10">
                <Users size={32} className="mx-auto mb-2" style={{ color: 'hsl(var(--muted-foreground))' }} />
                <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>Nema evidentiranih zaposlenih</p>
              </div>
            ) : (
              client.employees.map(emp => (
                <div
                  key={emp.id}
                  className="p-3 rounded-xl border"
                  style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                      style={{ backgroundColor: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))' }}
                    >
                      {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>
                        {emp.name}
                      </p>
                      <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        {emp.position}
                      </p>
                      <div className="mt-1.5 flex flex-col gap-0.5">
                        <a
                          href={`tel:${emp.phone}`}
                          className="text-xs flex items-center gap-1 hover:underline"
                          style={{ color: 'hsl(var(--accent))' }}
                        >
                          <Phone size={11} /> {emp.phone}
                        </a>
                        <a
                          href={`mailto:${emp.email}`}
                          className="text-xs flex items-center gap-1 hover:underline truncate"
                          style={{ color: 'hsl(var(--accent))' }}
                        >
                          <Mail size={11} /> {emp.email}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Equipment tab */}
        {activeTab === 'equipment' && (
          <div className="space-y-3">
            {equipment.length === 0 ? (
              <div className="text-center py-10">
                <Monitor size={32} className="mx-auto mb-2" style={{ color: 'hsl(var(--muted-foreground))' }} />
                <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>Nema evidentirane opreme</p>
              </div>
            ) : (
              equipment.map(eq => (
                <div
                  key={eq.id}
                  className="p-3 rounded-xl border"
                  style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
                    >
                      {EQ_ICONS[eq.type] || <Package size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>
                          {eq.brand} {eq.model}
                        </p>
                        <span
                          className="badge text-xs"
                          style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
                        >
                          {eq.type}
                        </span>
                      </div>
                      <p className="text-xs mono mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        S/N: {eq.serial}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        <span>{eq.purchaseYear}. god.</span>
                        {eq.notes && <span className="truncate">· {eq.notes}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Interventions tab */}
        {activeTab === 'interventions' && (
          <div className="space-y-3">
            {interventions.length === 0 ? (
              <div className="text-center py-10">
                <FileText size={32} className="mx-auto mb-2" style={{ color: 'hsl(var(--muted-foreground))' }} />
                <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>Nema evidentiranih intervencija</p>
              </div>
            ) : (
              interventions
                .sort((a, b) => b.date.localeCompare(a.date))
                .map(inv => {
                  const ctColor = CONNECTION_TYPE_COLORS[inv.connectionType];
                  const stColor = STATUS_COLORS[inv.status];
                  const [y, m, d] = inv.date.split('-');
                  return (
                    <div
                      key={inv.id}
                      className="p-3 rounded-xl border"
                      style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <span
                          className={`badge ${ctColor.bg} ${ctColor.text}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${ctColor.dot}`} />
                          {inv.connectionType}
                        </span>
                        <span className={`badge ${stColor.bg} ${stColor.text}`}>
                          {inv.status}
                        </span>
                      </div>
                      <p className="text-sm leading-snug line-clamp-2" style={{ color: 'hsl(var(--foreground))' }}>
                        {inv.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        <span>{`${d}.${m}.${y}`} u {inv.time}</span>
                        <span>· {inv.duration} min</span>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex-shrink-0" style={{ color: 'hsl(var(--muted-foreground))' }}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.06em' }}>
          {label}
        </p>
        <div className="text-sm mt-0.5" style={{ color: 'hsl(var(--foreground))' }}>
          {value}
        </div>
      </div>
    </div>
  );
}